<template>
  <section class="cso-panel" :class="scope.clsPrefix">
    <div class="panel-head">
      <div class="head-left">
        <span class="panel-title">供应链影响分析·风险因子传导网络</span>
        <button class="price-management-btn" @click="$emit('open-price-management')">自定义涨价信息</button>
        <button class="smart-analysis-btn" @click="handleSmartAnalysis">智能分析</button>
      </div>
      <div class="head-filters">
        <el-select v-model="filters.impactSort" size="mini" class="filter-select" @change="handleFilterChange">
          <el-option label="影响降序" value="desc" />
          <el-option label="影响升序" value="asc" />
        </el-select>
        <el-select v-model="filters.timeCycle" size="mini" class="filter-select" @change="handleFilterChange">
          <el-option label="本月度" value="monthly" />
          <el-option label="本季度" value="quarter" />
        </el-select>
        <el-select
          v-model="filters.customers"
          multiple
          collapse-tags
          collapse-tags-tooltip
          clearable
          size="mini"
          class="filter-select filter-select-multi"
          placeholder="客户"
          @change="handleFilterChange"
        >
          <el-option
            v-for="c in customerOptions"
            :key="c.nodeCode"
            :label="c.label"
            :value="c.nodeCode"
          />
        </el-select>
        <button class="reset-btn" @click="handleReset">重置</button>
      </div>
    </div>
    <div class="panel-sub">
      从风险因子出发，逐层传导至供应商、物料、产品与客户，支持按客户/物料/时间/影响金额筛选，点击节点可进行 AI 分析。
    </div>
    <div class="chart-body" v-loading="loading">
      <div :id="chartId" class="sankey-chart">
        <div class="chart-watermark">ZSD19849848</div>
      </div>
    </div>
    <div class="chart-legend">
      <span class="legend-item" v-for="cat in categoryLegend" :key="cat.name">
        <i class="legend-dot" :style="{ background: cat.color }"></i>
        {{ cat.label }} {{ cat.count }} {{ cat.unit }}
      </span>
      <span class="legend-impact">预计影响 <strong>¥{{ formatAmount(stats.impactAmount) }}</strong></span>
    </div>
    <div class="impact-slider">
      <span class="slider-label">影响金额</span>
      <span class="slider-min">¥{{ formatAmount(amountRange[0]) }}</span>
      <el-slider
        v-model="sliderValue"
        range
        :min="0"
        :max="sliderMax"
        :step="10"
        class="slider-control"
        @change="handleAmountChange"
      />
      <span class="slider-max">¥{{ formatAmount(sliderValue[1]) }}</span>
    </div>
  </section>
</template>

<script>
import * as echarts from 'echarts'
import store from '@/store'
import { getSupplyChainSankeyGraph, getSupplyChainCustomerList } from '@/api/supply-chain'

// 桑基图列的业务流向顺序（从左到右）
const CATEGORY_ORDER = ['risk', 'supplier', 'material', 'product', 'customer']

// 「影响金额」筛选的默认下限（单位：元）。
// 账号 czwl@cosmoplat.cn 的数据量过大、链路重叠到无法阅读，故把它的默认下限
// 抬到 1 万，只展示影响金额在 1 万以上的链路；其余账号默认 0 = 全部展示。
// 注意：这只是默认下限，滑轨最左侧仍是 0，用户可把左手柄拖回 0 查看全量 ——
// 这正是「1 万以上查不到数据」时的人工兜底（原先的自动放宽逻辑已移除）。
const DEFAULT_AMOUNT_MIN = 0
const LARGE_VOLUME_USER_CODE = 'czwl@cosmoplat.cn'
const LARGE_VOLUME_AMOUNT_MIN = 10000

// 「影响金额」筛选上限（单位：元），所有账号统一，含义是「不设上限」。
// 必须显著大于任何真实数据：上限会随请求下发给后端并截断返回结果，一旦取小了，
// 超出部分的链路会从图中永久消失。
// 取 1 万亿（实测后端可正常接受，不会溢出报错），业务上不存在触达可能，等价于不设上限。
// 注意：这个哨兵值只在「用户主动拖动过右手柄」之后才会被替换成真实数值；
// 滑块内部的钳位回灌不会改写它，见 sliderValue 的 setter。
const AMOUNT_MAX = 1000000000000

// 滑轨的占位可视上限（单位：元）。
// 下限为 0 的非目标账号，数据回来之前没有任何刻度上界，需要一个占位量把滑轨撑开，
// 否则 :max 会等于 0，element-ui 算滑块位置时会出现 0/0 的 NaN。
// 只在「数据最大值尚未取到」（impactMax === 0）时生效，取到后交给真实值接管，
// 保证滑轨仍随数据收缩。
const DEFAULT_RAIL_MAX = 10000000

/**
 * 解析当前登录用户的 userCode。
 * 取不到时返回空串，调用方按「非目标账号」处理。
 */
function resolveUserCode() {
  try {
    const userInfo = store.getters.userInfo || {}
    const code = userInfo.userCode
    return code === null || code === undefined ? '' : String(code).trim()
  } catch (e) {
    return ''
  }
}

/**
 * 当前账号下「影响金额」筛选应使用的默认下限（单位：元）。
 * userCode 为邮箱形态，大小写不敏感，故统一转小写比较。
 * 用户信息解析失败时不能静默降级 —— 那会表现为「改动没生效」，故显式告警。
 *
 * 只决定下限；上限对所有账号统一是 AMOUNT_MAX（不设上限）。
 * 上限不取自接口返回数据的最大值，否则会与「上限截断返回结果」形成循环依赖：
 * 首包上限取小一次，超限链路就永久不可见且无法自愈。
 */
function getDefaultAmountMin() {
  const userCode = resolveUserCode()
  if (!userCode) {
    console.warn('[CsoSankeyChart] 未能解析 userCode，影响金额筛选回退为默认下限 0')
    return DEFAULT_AMOUNT_MIN
  }
  return userCode.toLowerCase() === LARGE_VOLUME_USER_CODE
    ? LARGE_VOLUME_AMOUNT_MIN
    : DEFAULT_AMOUNT_MIN
}

/** 默认区间：[默认下限, 不设上限] */
function buildDefaultAmountRange() {
  return [getDefaultAmountMin(), AMOUNT_MAX]
}

// 节点列颜色（从左到右：风险因子 -> 供应商 -> 物料 -> 产品 -> 客户）
const CATEGORY_COLORS = {
  risk: '#DC2626', // 红色（接口返回的是 risk，原 risk_factor 改名对齐）
  supplier: '#F97316', // 橙色
  material: '#4A5FF3', // 蓝紫主色
  product: '#1D4ED8', // 深蓝
  customer: '#4B5563' // 深灰
}

// 按"链路起止节点类别"生成统一的渐变对象缓存
const LINK_GRADIENT_CACHE = new Map()
const buildLinkGradient = (sourceCat, targetCat) => {
  const key = `${sourceCat || ''}__${targetCat || ''}`
  if (LINK_GRADIENT_CACHE.has(key)) return LINK_GRADIENT_CACHE.get(key)
  const sourceColor = CATEGORY_COLORS[sourceCat] || '#9ca3af'
  const targetColor = CATEGORY_COLORS[targetCat] || '#9ca3af'
  const hexToRgba = (hex, a) => {
    const h = hex.replace('#', '')
    const r = parseInt(h.substring(0, 2), 16)
    const g = parseInt(h.substring(2, 4), 16)
    const b = parseInt(h.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${a})`
  }
  const gradient = {
    type: 'linear',
    x: 0, y: 0, x2: 1, y2: 0,
    colorStops: [
      { offset: 0, color: hexToRgba(sourceColor, 0.35) },
      { offset: 1, color: hexToRgba(targetColor, 0.85) }
    ]
  }
  LINK_GRADIENT_CACHE.set(key, gradient)
  return gradient
}

// 接口 categories[].name -> 中文标签
// 覆盖：接口已知值。其它未知分类会回退到「英文原名」。
const CATEGORY_LABELS = {
  risk: { label: '风险因子', unit: '项' },
  supplier: { label: '影响供应商', unit: '家' },
  material: { label: '影响物料', unit: '种' },
  product: { label: '影响产品', unit: '个' },
  customer: { label: '影响客户', unit: '家' }
}

export default {
  name: 'CsoSankeyChart',
  data() {
    return {
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-cso-sankey'
      },
      chartId: 'cso_sankey_chart_' + Math.random().toString(36).substr(2, 9),
      chartInstance: null,
      loading: false,
      filters: {
        impactSort: 'desc',
        // 后端 timeCycle 只支持 monthly / quarter 两个值
        timeCycle: 'monthly',
        customers: []
      },
      // 默认下限按账号取值（见 getDefaultAmountMin），上限统一「不设上限」
      amountRange: buildDefaultAmountRange(),
      rawNodes: [],
      rawLinks: [],
      categoryLegend: [],
      customerOptions: [],
      stats: {
        impactAmount: 12044,
        // 返回数据里「客户节点 costIncrease 的最大值」，仅用作滑轨刻度（见 sliderMax），
        // 不作为请求参数下发。接口首次回包时由 updateStats 写入并锁定，之后不再随筛选变化。
        // 未取到时是 0，表示「无数据约束」——这里不能放占位哨兵值：那会把滑轨先撑到 0~1000 万，
        // 等接口回来才收缩，首屏两个手柄会挤在最左边；若请求失败或空包则永远摊不开。
        impactMax: 0,
        // 是否已经由真实接口数据确定过 impactMax；锁定后不再随筛选更新
        impactMaxLocked: false
      },
      resizeHandler: null,
      resizeObserver: null,
      // 缓存当前 series 用到的节点 / 链路，避免 setOption 时依赖 inst.getOption 归一化对象
      chartSeriesData: [],
      chartSeriesLinks: [],
      // 当前 hover 的关联集合；null 表示未悬浮（保持全部亮）
      currentRelated: null,
      // 高亮渐显渐失的 RAF 句柄（用于打断旧动画）
      highlightAnimId: 0,
      // 节点 / 链路 opacity 渐变目标（用于 RAF 插值）
      highlightTargets: null,
      // 拖拽状态跟踪：防止 RAF setOption 在拖拽期间重置图表状态
      isDragging: false,
      // hover 高亮延迟定时器：给 mousedown 留出时间窗口，避免高亮与拖拽竞争
      highlightDelayTimer: null,
      // document mouseup 清理句柄（拖拽可能结束在图表外部）
      docMouseUpHandler: null,
      // 桑基图节点 metadata 英文 key -> 中文描述映射
      metadataLabelMap: {
        material: {
          priceIncrease: '涨价幅度',
          inventoryQuantity: '库存数量',
          name: '物料名称',
          materialCode: '物料编码',
          unit: '单位',
          quotaQuantity: '配额数量',
          quotaRatio: '配额占比'
        },
        product: {
          productModel: '产品型号',
          grossMargin: '毛利率(%)',
          rawMaterialCost: '原材料成本',
          processingFee: '加工费'
        },
        // 接口当前分类叫 risk，这里跟 category 同步
        risk: {
          impactLevel: '影响等级',
          riskType: '风险类型',
          relatedMonitorMarket: '关联监测市场',
          retrievalTime: '检索时间',
          dataSource: '数据来源'
        },
        supplier: {
          supplierName: '供应商名称',
          qualityScore: '质量评分',
          supplierStatus: '供应商状态',
          supplierLevel: '供应商等级',
          supplierCode: '供应商编码',
          deliveryScore: '交付评分',
          performanceScore: '绩效评分'
        },
        customer: {
          code: '客户编码',
          address: '客户地址',
          taxId: '税号',
          name: '客户名称',
          costIncrease: '预计影响金额'
        }
      }
    }
  },
  computed: {
    selectedCustomerCodes() {
      if (!this.filters.customers || this.filters.customers.length === 0) return null
      return this.filters.customers
    },
    /**
     * 滑块可视上限（:max），只影响刻度，不影响请求参数。
     * = max(数据最大值, 区间下限, 数据未取到时的占位值)
     * - 数据最大值（impactMax）：滑轨上限由接口响应算出，见 updateStats。
     *   未取到真实数据时它是 0（见 data 注释），此时不参与计算。
     * - 区间下限（amountRange[0]）：下限比数据最大值还大时（如目标账号下限 1 万、
     *   但返回数据最大值只有几千），滑块 value 会超出 :max 而渲染错位。
     * - 占位值（DEFAULT_RAIL_MAX）：仅在「数据最大值尚未取到且下限为 0」时兜底。
     *   此时若不加占位，:max 会是 0，element-ui 算滑块位置时出现 0/0 的 NaN。
     *   下限非 0 的账号由下限本身兜住，不需要占位。
     * 三者取最大，保证 :max 恒 > 0，且取到数据后交给真实值接管（滑轨随数据收缩）。
     */
    sliderMax() {
      const placeholder = this.stats.impactMax > 0 ? 0 : DEFAULT_RAIL_MAX
      return Math.max(this.stats.impactMax, this.amountRange[0], placeholder)
    },
    /**
     * 滑块绑定值（仅为显示，请求参数仍取 amountRange）。
     * 所有账号的 amountRange[1] 恒为 AMOUNT_MAX（1 万亿），直接喂给滑块会渲染错位，
     * 刻度标签也会显示成无意义的巨额数字。故显示时收敛到 sliderMax，含义仍是「不设上限」。
     * 取值走 set 写回 amountRange：用户一旦拖动，上限就变成他选的真实数值。
     */
    sliderValue: {
      get() {
        return [this.amountRange[0], Math.min(this.amountRange[1], this.sliderMax)]
      },
      set(val) {
        // element-ui 的 range 滑块会「钳位后回灌」：mounted 时执行
        //   firstValue  = Math.max(min, value[0])
        //   secondValue = Math.min(max, value[1])
        // 再经 firstValue/secondValue 的 watcher 触发 $emit('input', [minValue, maxValue])。
        // 这里 value[1] 是 1 万亿哨兵，会被 :max（占位 1000 万或真实 impactMax）截断，
        // 于是「不设上限」被悄悄改写成 1000 万并作为 costIncreaseMax 下发 —— 上限一旦取小，
        // 超限链路就永久不可见。:max 后续变化（数据回包）也会重复触发同一条回灌路径。
        //
        // 回灌的判据：来的值恰好等于当前 getter 会产出的显示值，即滑块内部状态没有产生
        // 任何用户可见的变化。此时原样保留 amountRange[1] 的哨兵值，不下发假上限。
        // 真实拖动必然让 val 偏离当前显示值（把右手柄拖到轨道最右端 = 不限上限，
        // 语义本就等价于哨兵值），故不会被这条判据吞掉。
        const displayed = [this.amountRange[0], Math.min(this.amountRange[1], this.sliderMax)]
        if (val[0] === displayed[0] && val[1] === displayed[1]) return
        this.amountRange = val
      }
    }
  },
  mounted() {
    // 统一延后到下一个 microtask 执行，避免在 Vue 主渲染流程中同步触发 setOption
    this.$nextTick(() => {
      this.fetchCustomerList()
      this.fetchGraph()
      this.resizeHandler = this.handleResize.bind(this)
      window.addEventListener('resize', this.resizeHandler)
      // 监听容器尺寸变化（缩放 / 折叠侧栏 / 路由切换都会触发），
      // 仅靠 window.resize 会漏掉"容器尺寸变了但窗口没变"的情况
      const dom = document.getElementById(this.chartId)
      if (dom && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          // resize 也通过 nextTick 延后，防止与其它主流程 setOption 抢同一帧
          this.$nextTick(() => {
            if (this.chartInstance) this.chartInstance.resize()
          })
        })
        this.resizeObserver.observe(dom)
      }
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.docMouseUpHandler) {
      document.removeEventListener('mouseup', this.docMouseUpHandler)
      this.docMouseUpHandler = null
    }
    clearTimeout(this.highlightDelayTimer)
    if (this.highlightAnimId) {
      cancelAnimationFrame(this.highlightAnimId)
      this.highlightAnimId = 0
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  },
  methods: {
    async fetchGraph() {
      this.loading = true
      try {
        const res = await getSupplyChainSankeyGraph(this.buildQueryParams())
        if (res && res.code === 0 && res.data) {
          this.rawNodes = res.data.nodes || []
          this.rawLinks = res.data.links || []
          this.buildLegend(res.data.categories || [])
          this.updateStats()
          this.$nextTick(() => this.drawChart())
        } else {
          this.rawNodes = []
          this.rawLinks = []
          this.categoryLegend = []
          this.$nextTick(() => this.drawChart())
        }
      } catch (error) {
        console.error('获取供应链影响图谱失败:', error)
        this.rawNodes = []
        this.rawLinks = []
        this.categoryLegend = []
        this.$nextTick(() => this.drawChart())
      } finally {
        this.loading = false
      }
    },
    buildQueryParams() {
      // 与新接口 /supplyChain/sankey/graph 对齐：
      //   impactSort / timeCycle / customerCodes / costIncreaseMin / costIncreaseMax
      const params = {
        impactSort: this.filters.impactSort,
        timeCycle: this.filters.timeCycle
      }
      if (this.filters.customers && this.filters.customers.length) {
        params.customerCodes = this.filters.customers.join(',')
      }
      if (this.amountRange && this.amountRange.length === 2) {
        const [min, max] = this.amountRange
        params.costIncreaseMin = min
        params.costIncreaseMax = max
      }
      return params
    },
    async fetchCustomerList() {
      try {
        const res = await getSupplyChainCustomerList()
        if (res && res.code === 0 && res.data && res.data.list) {
          this.customerOptions = res.data.list.map((item) => ({
            nodeCode: item.customerCode,
            label: item.customerName
          }))
          // 清理已不存在的选中项
          if (this.filters.customers && this.filters.customers.length) {
            const valid = new Set(this.customerOptions.map((c) => c.nodeCode))
            this.filters.customers = this.filters.customers.filter((c) => valid.has(c))
          }
        }
      } catch (error) {
        console.error('获取客户列表失败:', error)
      }
    },
    buildLegend(categories) {
      // categories 来自接口；每个 { name, label }
      const list = (categories.length
        ? categories
        : Object.keys(CATEGORY_LABELS).map((k) => ({ name: k, label: CATEGORY_LABELS[k].label }))
      )
      const filtered = this.filterByCustomers()
      this.categoryLegend = list.map((cat) => {
        const count = filtered.nodes.filter((n) => n.category === cat.name).length
        const meta = CATEGORY_LABELS[cat.name] || { label: cat.label, unit: '个' }
        return {
          name: cat.name,
          label: meta.label,
          unit: meta.unit,
          count,
          color: CATEGORY_COLORS[cat.name] || '#9ca3af'
        }
      })
    },
    updateStats() {
      const filtered = this.filterByCustomers()
      // 所有「客户节点」的 costIncrease（元）
      const customerCosts = filtered.nodes
        .filter((n) => n.category === 'customer')
        .map((n) => Number(n.metadata && n.metadata.costIncrease) || 0)
      // 影响金额范围的最大值：只在「第一次」接口数据回包时按全量客户节点取最大值并锁定，
      // 之后用户切换时间/客户/金额筛选都不再更新 slider 上限，避免滑块跟随筛选跳变。
      // 注意：这里不再改写 amountRange。
      // 请求上限恒为 AMOUNT_MAX（不设上限），滑轨上限纯由上面这个 impactMax 决定，
      // 两者解耦 —— 不会出现「上限截断返回数据、返回数据又决定上限」的循环依赖。
      // 且 updateStats 每次回包都会执行，在此赋值会把用户拖动滑块的选择一并冲掉。
      if (!this.stats.impactMaxLocked) {
        const maxCustomerCost = customerCosts.length ? Math.max(...customerCosts) : 0
        // 取不到客户节点时置 0（= 无数据约束），不要回退成一千万：
        // 那会把滑轨重新撑到 0~1000 万，目标账号的 1 万起步区间又会挤在最左边。
        this.stats.impactMax = maxCustomerCost
        this.stats.impactMaxLocked = true
      }

      // 预计影响（图例展示用）= 当前筛选下所有客户节点 costIncrease 之和
      const customerImpact = customerCosts.reduce((sum, v) => sum + v, 0)
      if (customerImpact > 0) {
        this.stats.impactAmount = customerImpact
        return
      }
      // 兜底：可见链路 value 累加
      const linkTotal = filtered.links.reduce((sum, l) => sum + (Number(l.value) || 0), 0)
      if (linkTotal > 0) {
        this.stats.impactAmount = linkTotal
      }
    },
    drawChart() {
      const dom = document.getElementById(this.chartId)
      if (!dom) return
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(dom)
      }
      const option = this.buildChartOption()
      // 缓存当前 series 真正使用的数据 + 链路，供 hover 高亮/淡化用
      this.chartSeriesData = (option.series && option.series[0] && option.series[0].data) || []
      this.chartSeriesLinks = (option.series && option.series[0] && option.series[0].links) || []
      this.currentRelated = null
      // 初次渲染的"当前 opacity"快照：节点 1、链路 0.55（与 series 默认值一致）
      const initByName = new Map()
      const initByLinkKey = new Map()
      this.chartSeriesData.forEach((n) => initByName.set(n.name, { nodeOpacity: 1, labelOpacity: 1 }))
      this.chartSeriesLinks.forEach((l) => initByLinkKey.set(this.linkKey(l), { linkOpacity: 0.55 }))
      this.lastRenderedOpacities = { byName: initByName, byLinkKey: initByLinkKey }
      // 如果有未完成的 RAF，取消
      if (this.highlightAnimId) {
        cancelAnimationFrame(this.highlightAnimId)
        this.highlightAnimId = 0
      }
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.chartInstance) return
          this.chartInstance.setOption(option, true)
          this.chartInstance.off('click')
          this.chartInstance.on('click', this.handleNodeClick)
          // 鼠标悬浮：节点 / 链路都触发关联高亮
          this.chartInstance.off('mouseover', this.handleSankeyMouseOver)
          this.chartInstance.off('mouseout', this.handleSankeyMouseOut)
          this.chartInstance.on('mouseover', this.handleSankeyMouseOver)
          this.chartInstance.on('mouseout', this.handleSankeyMouseOut)
          // 拖拽状态跟踪：mousedown 开始拖拽 → 取消高亮动画 → 拖拽期间禁止 setOption
          this.chartInstance.off('mousedown', this.handleSankeyMouseDown)
          this.chartInstance.off('mouseup', this.handleSankeyMouseUp)
          this.chartInstance.on('mousedown', this.handleSankeyMouseDown)
          this.chartInstance.on('mouseup', this.handleSankeyMouseUp)
          // 兜底：document mouseup 确保拖拽结束在图表外部时也能清理状态
          if (this.docMouseUpHandler) {
            document.removeEventListener('mouseup', this.docMouseUpHandler)
          }
          this.docMouseUpHandler = () => {
            if (this.isDragging) {
              this.isDragging = false
              // 拖拽结束在图表外部时也要同步位置
              this.$nextTick(() => this.syncNodePositionsFromChart())
            }
          }
          document.addEventListener('mouseup', this.docMouseUpHandler)
        }, 0)
      })
    },
    handleSankeyMouseOver(params) {
      if (!this.chartInstance || !params) return
      // 拖拽期间不触发高亮
      if (this.isDragging) return
      let related = null
      if (params.dataType === 'node' && params.data && params.data.name) {
        related = this.collectRelatedByNode(params.data.name)
      } else if (params.dataType === 'edge' && params.data) {
        related = this.collectRelatedByLink(params.data)
      }
      if (related) {
        this.currentRelated = related
        // 加 150ms 延迟再触发高亮动画，留出时间窗口给 mousedown 启动拖拽。
        // 如果用户只是悬停查看，150ms 几乎无感；如果用户立即 mousedown 拖拽，
        // mousedown 会取消此定时器，避免 RAF setOption 与拖拽冲突。
        clearTimeout(this.highlightDelayTimer)
        this.highlightDelayTimer = setTimeout(() => {
          this.highlightDelayTimer = null
          if (!this.isDragging) {
            this.applyHighlight(related)
          }
        }, 150)
      }
    },
    handleSankeyMouseOut(params) {
      if (!this.chartInstance) return
      // 清除待执行的高亮延迟
      clearTimeout(this.highlightDelayTimer)
      this.highlightDelayTimer = null
      // 拖拽期间不清除高亮（保留当前视觉状态），拖拽结束后由 mouseup 处理
      if (this.isDragging) return
      // 鼠标从节点 / 链路 移开就清掉淡化，恢复全亮
      this.currentRelated = null
      this.clearHighlight()
    },
    /**
     * 节点 mousedown：标记拖拽开始，立即取消所有高亮相关的定时器与动画。
     * 拖拽期间 RAF 动画的 setOption 调用会被跳过，避免重置 ECharts 内部拖拽状态。
     */
    handleSankeyMouseDown(params) {
      if (params && params.seriesType === 'sankey') {
        this.isDragging = true
        // 取消待执行的高亮延迟定时器（用户在 hover 后立即按下的场景）
        clearTimeout(this.highlightDelayTimer)
        this.highlightDelayTimer = null
        // 取消正在运行的 RAF 渐变动画
        if (this.highlightAnimId) {
          cancelAnimationFrame(this.highlightAnimId)
          this.highlightAnimId = 0
        }
      }
    },
    /**
     * 节点 mouseup：标记拖拽结束，恢复正常高亮交互。
     */
    handleSankeyMouseUp(params) {
      if (this.isDragging) {
        this.isDragging = false
        // 拖拽结束后，从 ECharts 实例同步节点位置到 chartSeriesData 缓存，
        // 确保后续 RAF 高亮动画 setOption 时带上正确的位置，不再弹回原位。
        this.$nextTick(() => this.syncNodePositionsFromChart())
      }
    },
    /**
     * 从 ECharts 实例读取当前节点位置（包含拖拽偏移），回写到 chartSeriesData 缓存。
     * 这样后续 RAF 动画重建节点数据时会带上正确的 x/y，不会让节点弹回初始位置。
     */
    syncNodePositionsFromChart() {
      const inst = this.chartInstance
      if (!inst) return
      try {
        const opt = inst.getOption()
        const seriesData = opt && opt.series && opt.series[0] && opt.series[0].data
        if (!seriesData || !seriesData.length) return
        const posByName = new Map()
        seriesData.forEach((d) => {
          if (d && d.name) {
            posByName.set(d.name, { x: d.x, y: d.y })
          }
        })
        if (!posByName.size) return
        this.chartSeriesData = this.chartSeriesData.map((n) => {
          const pos = posByName.get(n.name)
          if (pos) {
            return { ...n, x: pos.x, y: pos.y }
          }
          return n
        })
      } catch (e) {
        // getOption 可能因图表未就绪而失败，静默忽略
      }
    },
    buildChartOption() {
      // 强制指定节点列位置：风险因子 -> 供应商 -> 物料 -> 产品 -> 客户
      const layerIndex = {}
      CATEGORY_ORDER.forEach((cat, idx) => { layerIndex[cat] = idx })

      const filtered = this.filterByCustomers()

      // 节点：保证节点 name 与链路 source/target 完全一致；过滤掉无入链/无出链的孤立点
      const usedNodeNames = new Set()
      filtered.links.forEach((l) => {
        if (l.source) usedNodeNames.add(l.source)
        if (l.target) usedNodeNames.add(l.target)
      })
      const visibleNodes = filtered.nodes.filter((n) => usedNodeNames.has(n.name))

      // 给每个节点一个 value（链路聚合），避免 ECharts 因节点缺 value 报错
      const inByNode = new Map()
      const outByNode = new Map()
      filtered.links.forEach((l) => {
        const v = Number(l.value) || 0
        if (l.target) inByNode.set(l.target, (inByNode.get(l.target) || 0) + v)
        if (l.source) outByNode.set(l.source, (outByNode.get(l.source) || 0) + v)
      })
      const nodeValue = (name) => Math.max(inByNode.get(name) || 0, outByNode.get(name) || 0) || 1

      // 节点构造：
      //   name 必须 = 拼链 key（link.source/target）
      //   displayName 是实际显示字段（viewName）
      const nodes = visibleNodes.map((n) => ({
        name: n.name,
        displayName: n.viewName || n.name,
        itemStyle: {
          color: CATEGORY_COLORS[n.category] || '#9ca3af',
          borderColor: 'rgba(255,255,255,0.6)',
          borderWidth: 0
        },
        depth: layerIndex[n.category] != null ? layerIndex[n.category] : 0,
        value: nodeValue(n.name),
        category: n.category,
        nodeCode: n.nodeCode,
        metadata: n.metadata
      }))
      // 节点名(拼链 key) -> 节点对象 映射
      const nodeNameToNode = new Map(filtered.nodes.map((n) => [n.name, n]))

      // 客户列排序：严格按接口返回的客户节点顺序（即 costIncrease 升降序）自上而下排列
      const sortedNodes = (() => {
        const rawCustomerOrder = this.rawNodes
          .filter((n) => n.category === 'customer')
          .map((n) => n.name)
        const customerSet = new Set(rawCustomerOrder)
        const customerSorted = nodes.filter((n) => customerSet.has(n.name))
        customerSorted.sort((a, b) => rawCustomerOrder.indexOf(a.name) - rawCustomerOrder.indexOf(b.name))
        const others = nodes.filter((n) => !customerSet.has(n.name))
        return [...customerSorted, ...others]
      })()

      // 链路构造：source/target 仅作拼链；保留 sourceName/targetName 给 tooltip
      const links = filtered.links
        .filter((l) => usedNodeNames.has(l.source) && usedNodeNames.has(l.target))
        .map((l) => {
          const sourceNode = nodeNameToNode.get(l.source)
          const targetNode = nodeNameToNode.get(l.target)
          const color = buildLinkGradient(sourceNode && sourceNode.category, targetNode && targetNode.category)
          return {
            source: l.source,
            target: l.target,
            sourceName: l.sourceName,
            targetName: l.targetName,
            value: Number(l.value) || 1,
            lineStyle: {
              color,
              opacity: 0.85,
              curveness: 0.5
            }
          }
        })
      sortedNodes.forEach((n) => {
        const inn = inByNode.get(n.name) || 0
        const outt = outByNode.get(n.name) || 0
        if (!inn && !outt) {
          n.value = 1
        } else {
          n.value = Math.max(inn, outt)
        }
      })
      return {
        tooltip: {
          trigger: 'item',
          triggerOn: 'mousemove',
          formatter: (params) => {
            if (params.dataType === 'node') {
              return this.formatNodeTooltip(params.data)
            }
            if (params.dataType === 'edge') {
              return this.formatLinkTooltip(params.data)
            }
            return ''
          }
        },
        series: [{
          type: 'sankey',
          // 用 focus: 'none' 关闭 ECharts 自带的 1-hop 高亮，避免和我们自定义的全链路高亮打架
          emphasis: { focus: 'none' },
          draggable: true,
          layoutIterations: 0,
          nodeGap: 12,
          nodeWidth: 24,
          sortLinks: true,
          left: 64,
          right: 128,
          top: 12,
          bottom: 12,
          label: {
            // 节点显示：用 viewName
            fontSize: 11,
            color: '#1f2937',
            fontWeight: 500,
            position: 'right',
            distance: 6,
            formatter: (params) => params.data.displayName || params.data.name
          },
          lineStyle: { color: 'gradient', curveness: 0.5, opacity: 0.55 },
          data: sortedNodes,
          links: links
        }]
      }
    },
    /**
     * 找出与某个节点相关的所有节点 + 链路（上下游全链路追溯）。
     * 用于鼠标悬浮节点时高亮关联元素、淡化其他元素。
     * @param {string} nodeName 拼链 key（nodes[].name）
     * @returns {{ nodes: Set<string>, linkKeys: Set<string> }}
     */
    collectRelatedByNode(nodeName) {
      const result = { nodes: new Set(), linkKeys: new Set() }
      if (!nodeName) return result
      result.nodes.add(nodeName)
      // 用 rawLinks 反向 + 正向都搜：上游链路 (link.target === nodeName) 与下游链路 (link.source === nodeName)
      const upQueue = [nodeName]
      const downQueue = [nodeName]
      const visitedUp = new Set([nodeName])
      const visitedDown = new Set([nodeName])
      while (upQueue.length) {
        const cur = upQueue.shift()
        for (const l of this.rawLinks) {
          if (l.target === cur) {
            const k = this.linkKey(l)
            if (k && !result.linkKeys.has(k)) {
              result.linkKeys.add(k)
              result.nodes.add(l.source)
              if (!visitedUp.has(l.source)) {
                visitedUp.add(l.source)
                upQueue.push(l.source)
              }
            }
          }
        }
      }
      while (downQueue.length) {
        const cur = downQueue.shift()
        for (const l of this.rawLinks) {
          if (l.source === cur) {
            const k = this.linkKey(l)
            if (k && !result.linkKeys.has(k)) {
              result.linkKeys.add(k)
              result.nodes.add(l.target)
              if (!visitedDown.has(l.target)) {
                visitedDown.add(l.target)
                downQueue.push(l.target)
              }
            }
          }
        }
      }
      return result
    },
    /**
     * 把链路对象归一化为字符串 key（用于跨引用比较）
     * 形如 `${source}__${target}`。同一对 source/target 在 rawLinks 与 chartSeriesLinks 里
     * 是不同的对象实例，但 key 一定相等，所以用字符串 key 比较即可命中。
     */
    linkKey(link) {
      if (!link || !link.source || !link.target) return ''
      return `${link.source}__${link.target}`
    },
    /**
     * 收集与某条 link 相关的所有节点 + 链路（整条链路全亮）。
     * 从 link.source 端做全链路 BFS（追溯上游），从 link.target 端做全链路 BFS（追溯下游），
     * 再加上当前这条 link 本身。整条链路上的所有节点 + 链路视为命中集合。
     * nodes: Set<string>（节点 name）；linkKeys: Set<string>（linkKey）
     */
    collectRelatedByLink(link) {
      const result = { nodes: new Set(), linkKeys: new Set() }
      if (!link || !link.source || !link.target) return result

      // 当前这条链路先加入
      const selfKey = this.linkKey(link)
      if (selfKey) result.linkKeys.add(selfKey)

      // source 端：沿上游链路 BFS（找 link.target === cur 的链路，方向是 source → ... → cur）
      const upFromSource = this.collectUpstream(link.source)
      upFromSource.nodes.forEach((n) => result.nodes.add(n))
      upFromSource.linkKeys.forEach((k) => result.linkKeys.add(k))

      // target 端：沿下游链路 BFS（找 link.source === cur 的链路，方向是 cur → ... → target）
      const downFromTarget = this.collectDownstream(link.target)
      downFromTarget.nodes.forEach((n) => result.nodes.add(n))
      downFromTarget.linkKeys.forEach((k) => result.linkKeys.add(k))

      // 两端节点 全部纳入
      result.nodes.add(link.source)
      result.nodes.add(link.target)

      return result
    },
    /**
     * 从某个节点出发，沿上游链路（link.target === cur）BFS 到尽头，
     * 收集所有上游节点 + 链路 key。用于 link hover 时 source 端的追溯。
     */
    collectUpstream(startName) {
      const nodes = new Set([startName])
      const linkKeys = new Set()
      const visited = new Set([startName])
      const queue = [startName]
      while (queue.length) {
        const cur = queue.shift()
        for (const l of this.rawLinks) {
          if (l.target === cur) {
            const k = this.linkKey(l)
            if (k && !linkKeys.has(k)) {
              linkKeys.add(k)
              nodes.add(l.source)
              if (!visited.has(l.source)) {
                visited.add(l.source)
                queue.push(l.source)
              }
            }
          }
        }
      }
      return { nodes, linkKeys }
    },
    /**
     * 从某个节点出发，沿下游链路（link.source === cur）BFS 到尽头，
     * 收集所有下游节点 + 链路 key。用于 link hover 时 target 端的追溯。
     */
    collectDownstream(startName) {
      const nodes = new Set([startName])
      const linkKeys = new Set()
      const visited = new Set([startName])
      const queue = [startName]
      while (queue.length) {
        const cur = queue.shift()
        for (const l of this.rawLinks) {
          if (l.source === cur) {
            const k = this.linkKey(l)
            if (k && !linkKeys.has(k)) {
              linkKeys.add(k)
              nodes.add(l.target)
              if (!visited.has(l.target)) {
                visited.add(l.target)
                queue.push(l.target)
              }
            }
          }
        }
      }
      return { nodes, linkKeys }
    },
    /**
     * 给当前 series 应用"高亮 / 淡化"效果（基于缓存的 chartSeriesData / chartSeriesLinks）。
     *   - 命中的节点 opacity = 1、label 颜色 = #1f2937
     *   - 命中的链路 opacity = 0.9
     *   - 其它节点 opacity = 0.18、label 颜色 = 'rgba(31,41,55,0.25)'
     *   - 其它链路 opacity = 0.06
     * notMerge=true 完整替换 data + links，确保 ECharts 一定按新值重绘
     */
    applyHighlight(related) {
      const inst = this.chartInstance
      if (!inst) return
      const hitNodes = related && related.nodes
      const hitLinkKeys = related && related.linkKeys
      // 构造每节点 / 每链路 的目标 opacity：
      //   - 命中：节点 1.0、链路 0.9
      //   - 未命中：节点 0.18、链路 0.06
      const nodeTarget = this.chartSeriesData.map((n) => {
        const isHit = hitNodes ? hitNodes.has(n.name) : false
        return {
          name: n.name,
          nodeOpacity: isHit ? 1 : 0.18,
          labelOpacity: isHit ? 1 : 0.25,
          labelColor: isHit ? '#1f2937' : 'rgba(31,41,55,0.25)'
        }
      })
      const linkTarget = this.chartSeriesLinks.map((l) => ({
        key: this.linkKey(l),
        linkOpacity: hitLinkKeys && hitLinkKeys.has(this.linkKey(l)) ? 0.9 : 0.06
      }))
      // 用 RAF 平滑插值到目标值（替代 setOption 直接改 opacity，实现渐显渐失）
      this.startHighlightTransition(nodeTarget, linkTarget)
    },
    /**
     * 还原所有节点 / 链路到默认亮度（仍然走 RAF 平滑插值，所以是渐失而非瞬变）
     */
    clearHighlight() {
      const inst = this.chartInstance
      if (!inst) return
      const nodeTarget = this.chartSeriesData.map((n) => ({
        name: n.name,
        nodeOpacity: 1,
        labelOpacity: 1,
        labelColor: '#1f2937'
      }))
      const linkTarget = this.chartSeriesLinks.map((l) => ({
        key: this.linkKey(l),
        linkOpacity: 0.55
      }))
      this.startHighlightTransition(nodeTarget, linkTarget)
    },
    /**
     * 用 requestAnimationFrame 平滑插值节点 / 链路 opacity。
     * 每一帧：
     *   1. 读当前渲染对象的实际 opacity（取自 inst.getModel().getSeriesByIndex(0) 或 inst._api）
     *   2. 线性插值到目标值（duration ms 内）
     *   3. 调 inst.setOption 把当前插值写回（merge 模式，不影响其它 series 字段）
     * 旧动画通过取消 RAF 句柄自然终结。
     */
    startHighlightTransition(nodeTargets, linkTargets) {
      const inst = this.chartInstance
      if (!inst) return

      // 取消上一次 RAF
      if (this.highlightAnimId) {
        cancelAnimationFrame(this.highlightAnimId)
        this.highlightAnimId = 0
      }

      const duration = 280 // ms
      const startNode = this.captureCurrentOpacities()
      const startTime = performance.now()

      // 目标值快速索引（按 name / linkKey）
      const nodeTargetByName = new Map()
      nodeTargets.forEach((t) => nodeTargetByName.set(t.name, t))
      const linkTargetByKey = new Map()
      linkTargets.forEach((t) => linkTargetByKey.set(t.key, t))

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3)

      const tick = () => {
        const elapsed = performance.now() - startTime
        const ratio = Math.min(1, elapsed / duration)
        const eased = easeOutCubic(ratio)

        // 节点当前帧 opacity
        const newData = this.chartSeriesData.map((n) => {
          const target = nodeTargetByName.get(n.name)
          const start = startNode.byName.get(n.name) || {
            nodeOpacity: 1,
            labelOpacity: 1
          }
          const curNodeOpacity = start.nodeOpacity + (target.nodeOpacity - start.nodeOpacity) * eased
          const curLabelOpacity = start.labelOpacity + (target.labelOpacity - start.labelOpacity) * eased
          const node = {
            name: n.name,
            displayName: n.displayName,
            depth: n.depth,
            value: n.value,
            category: n.category,
            nodeCode: n.nodeCode,
            metadata: n.metadata,
            itemStyle: {
              color: (n.itemStyle && n.itemStyle.color) || '#9ca3af',
              borderColor: 'rgba(255,255,255,0.6)',
              borderWidth: 0,
              opacity: Number(curNodeOpacity.toFixed(3))
            },
            label: {
              show: true,
              fontSize: 11,
              color: '#1f2937',
              fontWeight: 500,
              position: 'right',
              distance: 6,
              opacity: Number(curLabelOpacity.toFixed(3)),
              formatter: () => n.displayName || n.name
            }
          }
          // 保留拖拽后的节点位置：chartSeriesData 上缓存的 x/y 是拖拽后同步过来的，
          // 必须原样传给 setOption，否则 ECharts 会把节点放回布局算法计算的初始位置。
          if (n.x != null) node.x = n.x
          if (n.y != null) node.y = n.y
          return node
        })
        // 链路当前帧 opacity
        const newLinks = this.chartSeriesLinks.map((l) => {
          const k = this.linkKey(l)
          const target = linkTargetByKey.get(k)
          const start = startNode.byLinkKey.get(k) || { linkOpacity: 0.55 }
          const curLinkOpacity = start.linkOpacity + (target.linkOpacity - start.linkOpacity) * eased
          return {
            source: l.source,
            target: l.target,
            sourceName: l.sourceName,
            targetName: l.targetName,
            value: l.value,
            lineStyle: {
              color: (l.lineStyle && l.lineStyle.color) || 'gradient',
              curveness: 0.5,
              opacity: Number(curLinkOpacity.toFixed(3))
            }
          }
        })
        // 拖拽期间不调用 setOption，否则会重置 ECharts 内部拖拽状态导致节点弹回
        if (!this.isDragging) {
          inst.setOption(
            { series: [{ data: newData, links: newLinks }] },
            false
          )
        }

        // 把当前帧的 opacity 落库，作为下一次动画的插值起点
        const curByName = new Map()
        newData.forEach((d) => {
          curByName.set(d.name, {
            nodeOpacity: d.itemStyle.opacity,
            labelOpacity: d.label.opacity
          })
        })
        const curByLinkKey = new Map()
        newLinks.forEach((l) => {
          curByLinkKey.set(`${l.source}__${l.target}`, {
            linkOpacity: l.lineStyle.opacity
          })
        })
        this.lastRenderedOpacities = { byName: curByName, byLinkKey: curByLinkKey }

        if (ratio < 1) {
          this.highlightAnimId = requestAnimationFrame(tick)
        } else {
          this.highlightAnimId = 0
        }
      }
      this.highlightAnimId = requestAnimationFrame(tick)
    },
    /**
     * 从 ECharts 渲染模型中读出当前每个节点 / 链路的实际 opacity，作为插值起点。
     * ECharts 没有直接暴露元素级 opacity 的 API（getOption 只能拿到我们 setOption 写进去的值），
     * 所以这里采用"从我们自己最近一次写入的 chartSeriesData / chartSeriesLinks 派生"：
     * 节点：上一次写入时已经写了 opacity 在 itemStyle / label 里；缓存一份"当前 opacity"。
     * 如果拿不到（例如首次调用），就用默认 1 / 0.55 兜底。
     */
    captureCurrentOpacities() {
      const byName = new Map()
      const byLinkKey = new Map()
      const prev = this.lastRenderedOpacities
      if (prev) {
        prev.byName.forEach((v, k) => byName.set(k, v))
        prev.byLinkKey.forEach((v, k) => byLinkKey.set(k, v))
      } else {
        // 首次进入：节点默认 1、链路默认 0.55（与 series 初始 lineStyle.opacity 一致）
        this.chartSeriesData.forEach((n) => {
          byName.set(n.name, { nodeOpacity: 1, labelOpacity: 1 })
        })
        this.chartSeriesLinks.forEach((l) => {
          byLinkKey.set(this.linkKey(l), { linkOpacity: 0.55 })
        })
      }
      return { byName, byLinkKey }
    },
    filterByCustomers() {
      // 客户筛选已由后端 /supplyChain/sankey/graph 的 customerCodes 参数处理，
      // 前端不再做客户端 BFS 反向追溯，直接使用 API 返回的数据即可。
      return { nodes: this.rawNodes, links: this.rawLinks }
    },
    formatNodeTooltip(node) {
      const cat = CATEGORY_LABELS[node.category] || { label: node.category }
      const meta = node.metadata || {}
      const labels = (this.metadataLabelMap && this.metadataLabelMap[node.category]) || {}
      // tooltip 中不展示的字段
      const excludeKeys = new Set(['riskFactorId', 'supplierList', 'costIncrease3m', 'costIncrease1m'])
      // 节点显示名 = displayName（viewName）
      const title = node.displayName || node.name
      const lines = [`<b>${title}</b>`, `<span style="color:#8c9bab">${cat.label || node.category}</span>`]
      Object.keys(meta)
        .filter((k) => !excludeKeys.has(k))
        .slice(0, 6)
        .forEach((k) => {
          const label = labels[k] || k
          lines.push(`<span style="color:#8c9bab">${label}:</span> ${meta[k]}`)
        })
      return lines.join('<br/>')
    },
    formatLinkTooltip(link) {
      // 链路显示：sourceName → targetName（fallback 到拼链 key）
      const src = link.sourceName || link.source
      const tgt = link.targetName || link.target
      return `${src} → ${tgt}<br/>影响值: <b>${link.value}</b>`
    },
    handleNodeClick(params) {
      if (params.dataType === 'node') {
        this.$emit('node-click', params.data)
      }
    },
    handleSmartAnalysis() {
      // 时间范围 -> 描述文案
      const cycleLabelMap = {
        monthly: '本月度',
        quarter: '本季度'
      }
      const rangeLabel = cycleLabelMap[this.filters.timeCycle] || '本月度'

      const customerText = (this.filters.customers && this.filters.customers.length)
        ? `；客户筛选：${this.filters.customers.join(',')}`
        : ''

      const content = `请分析${rangeLabel}桑基图链路${customerText}`

      // 1) 与 CosmoClawInput / agentLayout 一致的兼容事件（保留以防外层仍监听）
      this.$root.$emit('cosmo-claw-send-external', content)

      this.$emit('smart-analysis', {
        type: 'enterpriseWarRoom',
        content
      })
    },
    handleFilterChange() {
      this.fetchGraph()
    },
    handleAmountChange() {
      this.fetchGraph()
    },
    handleReset() {
      this.filters.impactSort = 'desc'
      this.filters.timeCycle = 'monthly'
      this.filters.customers = []
      // 重置时解锁 impactMax，让 updateStats 重新取数据最大值来恢复滑块可视上限
      this.stats.impactMaxLocked = false
      // 区间按账号恢复（避免重置后目标账号又回到全量）
      this.amountRange = buildDefaultAmountRange()
      this.fetchGraph()
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },
    formatAmount(val) {
      const num = Number(val) || 0
      return num.toLocaleString('zh-CN')
    }
  }
}
</script>

<style lang="scss" scoped>
.rvc-cosmoplat-workspace-cso-sankey {
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .panel-head {
    background: #e8f0fe;
    color: #1e3a5f;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #d0dff8;
    gap: 12px;
    flex-wrap: wrap;
    border-radius: 4px 4px 0 0;
  }
  .head-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #1e3a5f;
  }
  .price-management-btn,
  .smart-analysis-btn {
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
  }
  .price-management-btn,
  .smart-analysis-btn {
    border: none;
    background: linear-gradient(270deg, #4a5ff3, #4e86ff);
    color: #fff;
  }
  .head-filters {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .filter-select {
    width: 110px;
  }
  .filter-select-multi {
    width: 180px;
  }
  .reset-btn {
    padding: 4px 10px;
    border: 1px solid #d0dff8;
    border-radius: 4px;
    background: #fff;
    color: #1e3a5f;
    font-size: 10px;
    cursor: pointer;
  }
  .reset-btn:hover {
    border-color: #4A5FF3;
    color: #4A5FF3;
  }
  .panel-sub {
    padding: 8px 14px;
    font-size: 10px;
    color: #6b7280;
    background: #f8fafc;
    border-bottom: 1px solid #edf1f5;
  }
  .chart-body {
    padding: 0;
    background: #fff;
    position: relative;
  }
  .sankey-chart {
    width: 100%;
    height: 480px;
    position: relative;
    overflow: hidden;
  }
  .chart-watermark {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
    background-image: repeating-linear-gradient(
      -30deg,
      transparent 0,
      transparent 140px,
      rgba(220, 38, 38, 0.04) 140px,
      rgba(220, 38, 38, 0.04) 145px
    );
    font-size: 22px;
    font-weight: 700;
    color: rgba(220, 38, 38, 0.06);
    letter-spacing: 6px;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    overflow: hidden;
  }
  .chart-watermark::before {
    content: 'ZSD19849848 ZSD19849848 ZSD19849848 ZSD19849848\A ZSD19849848 ZSD19849848 ZSD19849848 ZSD19849848\A ZSD19849848 ZSD19849848 ZSD19849848 ZSD19849848\A ZSD19849848 ZSD19849848 ZSD19849848 ZSD19849848\A ZSD19849848 ZSD19849848 ZSD19849848 ZSD19849848';
    white-space: pre;
    line-height: 180px;
    transform: rotate(-30deg) translate(-60px, -40px);
    display: block;
    width: 200%;
  }
  .chart-body >>> .echarts,
  .chart-body >>> #sankey-chart {
    position: relative;
    z-index: 1;
  }
  .chart-legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    padding: 10px 14px;
    border-top: 1px solid #edf1f5;
    background: #f8fafc;
    font-size: 10px;
    color: #6b7280;
  }
  .legend-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .legend-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 2px;
  }
  .legend-impact {
    margin-left: auto;
    color: #4A5FF3;
    font-weight: 600;
  }
  .impact-slider {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 14px 12px;
    background: #f8fafc;
    border-top: 1px solid #edf1f5;
    font-size: 10px;
    color: #6b7280;
    border-radius: 0 0 4px 4px;
  }
  .slider-label {
    font-weight: 700;
    color: #1f2937;
    white-space: nowrap;
  }
  .slider-min,
  .slider-max {
    color: #4A5FF3;
    font-weight: 600;
    min-width: 60px;
  }
  .slider-control {
    flex: 1;
    margin: 0 8px;
  }
}
</style>
